<template>
  <div class="boundary-page">
    <div class="page-header">
      <h2 class="page-title">盘查边界管理</h2>
    </div>

    <filter-bar>
      <div class="filter-item">
        <span class="filter-label">核算年度</span>
        <el-date-picker
          v-model="year"
          type="year"
          size="small"
          placeholder="选择年份"
          value-format="yyyy"
          style="width: 120px"
        />
      </div>
      <div class="filter-item">
        <span class="filter-label">核算标准</span>
        <el-select v-model="standard" size="small" style="width: 160px">
          <el-option
            v-for="item in standards"
            :key="item"
            :label="item"
            :value="item"
          />
        </el-select>
      </div>
      <el-button type="primary" size="small" @click="loadData">查询</el-button>
    </filter-bar>

    <div class="stat-cards-row stat-cards-3">
      <stat-card
        title="纳入设施数"
        :today="summary.facilityCount.today"
        :annual="summary.facilityCount.annual"
        :unit="summary.facilityCount.unit"
        :annual-unit="summary.facilityCount.unit"
        icon="el-icon-office-building"
        color="#409EFF"
      />
      <stat-card
        title="排放源数"
        :today="summary.sourceCount.today"
        :annual="summary.sourceCount.annual"
        :unit="summary.sourceCount.unit"
        :annual-unit="summary.sourceCount.unit"
        icon="el-icon-s-flag"
        color="#E6A23C"
      />
      <stat-card
        title="已确认边界数"
        :today="summary.confirmedCount.today"
        :annual="summary.confirmedCount.annual"
        :unit="summary.confirmedCount.unit"
        :annual-unit="summary.confirmedCount.unit"
        icon="el-icon-circle-check"
        color="#67C23A"
      />
    </div>

    <div class="boundary-layout">
      <aside class="boundary-tree-panel">
        <div class="panel-title">
          <i class="el-icon-s-grid"></i> 组织层级
        </div>
        <el-tree
          :data="orgTree"
          node-key="id"
          default-expand-all
          highlight-current
          :current-node-key="currentNodeId"
          :props="{ label: 'label', children: 'children' }"
          @node-click="handleNodeClick"
        />
      </aside>

      <div class="boundary-content">
        <el-tabs v-model="activeTab">
          <el-tab-pane label="组织边界" name="org">
            <el-descriptions :column="2" border size="small">
              <el-descriptions-item label="边界方法">{{ orgBoundary.method }}</el-descriptions-item>
              <el-descriptions-item label="股权比例">{{ orgBoundary.equityRatio }}</el-descriptions-item>
              <el-descriptions-item label="生效日期">{{ orgBoundary.effectiveDate }}</el-descriptions-item>
              <el-descriptions-item label="核算标准">{{ orgBoundary.standard }}</el-descriptions-item>
              <el-descriptions-item label="边界说明" :span="2">{{ orgBoundary.description }}</el-descriptions-item>
            </el-descriptions>
          </el-tab-pane>

          <el-tab-pane label="运营边界" name="operation">
            <div class="table-toolbar">
              <el-button type="primary" size="small" icon="el-icon-plus" @click="openSourceDialog()">
                新增排放源
              </el-button>
            </div>
            <el-table :data="filteredEmissionSources" border stripe size="small">
              <el-table-column prop="name" label="排放源名称" min-width="160" />
              <el-table-column prop="scope" label="Scope" width="100" />
              <el-table-column prop="category" label="排放类别" width="140" />
              <el-table-column prop="facility" label="所属设施" width="140" />
              <el-table-column prop="statusLabel" label="状态" width="90" align="center">
                <template slot-scope="{ row }">
                  <el-tag :type="row.status === 'confirmed' ? 'success' : 'info'" size="mini">
                    {{ row.statusLabel }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="100" align="center" fixed="right">
                <template slot-scope="{ row }">
                  <el-button type="text" size="small" @click="openSourceDialog(row)">编辑</el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>

          <el-tab-pane label="纳入范围" name="facility">
            <el-table :data="facilities" border stripe size="small">
              <el-table-column prop="name" label="设施名称" width="160" />
              <el-table-column prop="plant" label="所属厂区" width="160" />
              <el-table-column prop="included" label="是否纳入" width="100" align="center">
                <template slot-scope="{ row }">
                  <el-tag :type="row.included ? 'success' : 'info'" size="mini">
                    {{ row.included ? '是' : '否' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="reason" label="纳入/排除理由" min-width="200" show-overflow-tooltip />
              <el-table-column label="操作" width="100" align="center" fixed="right">
                <template slot-scope="{ row }">
                  <el-button type="text" size="small" @click="openFacilityDialog(row)">编辑</el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>

    <el-dialog
      :title="sourceDialogTitle"
      :visible.sync="sourceDialogVisible"
      width="520px"
      @close="resetSourceForm"
    >
      <el-form ref="sourceForm" :model="sourceForm" label-width="100px" size="small">
        <el-form-item label="排放源名称" required>
          <el-input v-model="sourceForm.name" placeholder="请输入排放源名称" />
        </el-form-item>
        <el-form-item label="Scope" required>
          <el-select v-model="sourceForm.scope" placeholder="请选择" style="width: 100%">
            <el-option v-for="item in scopeOptions" :key="item" :label="item" :value="item" />
          </el-select>
        </el-form-item>
        <el-form-item label="排放类别" required>
          <el-select v-model="sourceForm.category" placeholder="请选择" style="width: 100%">
            <el-option v-for="item in categoryOptions" :key="item" :label="item" :value="item" />
          </el-select>
        </el-form-item>
        <el-form-item label="所属设施" required>
          <el-input v-model="sourceForm.facility" placeholder="请输入所属设施" />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="sourceForm.status" placeholder="请选择" style="width: 100%">
            <el-option
              v-for="item in statusOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="sourceDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" @click="saveSource">保存</el-button>
      </div>
    </el-dialog>

    <el-dialog title="编辑设施纳入" :visible.sync="facilityDialogVisible" width="480px">
      <el-form :model="facilityForm" label-width="120px" size="small">
        <el-form-item label="设施名称">
          <el-input v-model="facilityForm.name" disabled />
        </el-form-item>
        <el-form-item label="是否纳入">
          <el-switch v-model="facilityForm.included" />
        </el-form-item>
        <el-form-item label="纳入/排除理由">
          <el-input v-model="facilityForm.reason" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="facilityDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" @click="saveFacility">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import FilterBar from '@/components/common/FilterBar.vue'
import StatCard from '@/components/common/StatCard.vue'
import { getBoundaryData } from '@/api/modules/carbonAccounting'

export default {
  name: 'CarbonAccountingBoundary',
  components: { FilterBar, StatCard },
  data() {
    return {
      year: '2025',
      standard: 'GB/T 32150',
      standards: [],
      summary: {
        facilityCount: { today: 0, annual: 0, unit: '个' },
        sourceCount: { today: 0, annual: 0, unit: '个' },
        confirmedCount: { today: 0, annual: 0, unit: '个' }
      },
      orgTree: [],
      currentNodeId: '',
      orgBoundary: {},
      emissionSources: [],
      facilities: [],
      activeTab: 'org',
      scopeOptions: [],
      categoryOptions: [],
      statusOptions: [],
      sourceDialogVisible: false,
      sourceDialogTitle: '新增排放源',
      sourceForm: {
        id: null,
        name: '',
        scope: '',
        category: '',
        facility: '',
        status: 'draft'
      },
      facilityDialogVisible: false,
      facilityForm: {
        id: null,
        name: '',
        included: true,
        reason: ''
      }
    }
  },
  computed: {
    filteredEmissionSources() {
      if (!this.currentNodeId) return this.emissionSources
      const nodeLabel = this.findNodeLabel(this.orgTree, this.currentNodeId)
      if (!nodeLabel || nodeLabel.includes('集团')) return this.emissionSources
      return this.emissionSources.filter(item =>
        item.facility.includes(nodeLabel) ||
        (nodeLabel.includes('生产基地') && item.facility.includes(nodeLabel.replace('生产基地', '').trim() || '号'))
      )
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    async loadData() {
      const res = await getBoundaryData({ year: this.year, standard: this.standard })
      const data = res.data
      this.standards = data.standards
      this.summary = data.summary
      this.orgTree = data.orgTree
      this.currentNodeId = data.currentNodeId
      this.orgBoundary = data.orgBoundary
      this.emissionSources = data.emissionSources
      this.facilities = data.facilities
      this.scopeOptions = data.scopeOptions
      this.categoryOptions = data.categoryOptions
      this.statusOptions = data.statusOptions
    },
    handleNodeClick(node) {
      this.currentNodeId = node.id
    },
    findNodeLabel(tree, id) {
      for (const node of tree) {
        if (node.id === id) return node.label
        if (node.children) {
          const found = this.findNodeLabel(node.children, id)
          if (found) return found
        }
      }
      return ''
    },
    openSourceDialog(row) {
      if (row) {
        this.sourceDialogTitle = '编辑排放源'
        this.sourceForm = { ...row }
      } else {
        this.sourceDialogTitle = '新增排放源'
        this.resetSourceForm()
      }
      this.sourceDialogVisible = true
    },
    resetSourceForm() {
      this.sourceForm = {
        id: null,
        name: '',
        scope: '',
        category: '',
        facility: '',
        status: 'draft'
      }
    },
    saveSource() {
      const statusLabel = this.statusOptions.find(s => s.value === this.sourceForm.status)?.label || '草稿'
      if (this.sourceForm.id) {
        const idx = this.emissionSources.findIndex(s => s.id === this.sourceForm.id)
        if (idx >= 0) {
          this.$set(this.emissionSources, idx, { ...this.sourceForm, statusLabel })
        }
      } else {
        this.emissionSources.push({
          ...this.sourceForm,
          id: Date.now(),
          statusLabel
        })
      }
      this.sourceDialogVisible = false
      this.$message.success('保存成功')
    },
    openFacilityDialog(row) {
      this.facilityForm = { ...row }
      this.facilityDialogVisible = true
    },
    saveFacility() {
      const idx = this.facilities.findIndex(f => f.id === this.facilityForm.id)
      if (idx >= 0) {
        this.$set(this.facilities, idx, { ...this.facilityForm })
      }
      this.facilityDialogVisible = false
      this.$message.success('保存成功')
    }
  }
}
</script>

<style lang="less" scoped>
@import '~@/styles/variables.less';

.page-header {
  margin-bottom: 16px;

  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: @text-primary;
    margin: 0;
  }
}

.stat-cards-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.boundary-layout {
  display: flex;
  gap: 16px;
  background: @card-bg;
  border-radius: @card-radius;
  box-shadow: @card-shadow;
  padding: 16px;
  min-height: 520px;
}

.boundary-tree-panel {
  width: 260px;
  flex-shrink: 0;
  border: 1px solid @border-color;
  border-radius: 4px;
  padding: 12px;

  .panel-title {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
}

.boundary-content {
  flex: 1;
  min-width: 0;
}

.table-toolbar {
  margin-bottom: 12px;
}
</style>
